import { config } from '../config.ts';
import { typedAttempt } from '../lib/coach.ts';
import { sayKey, type FeedbackKey, type FeedbackLang } from '../lib/feedbackVoice.ts';
import { normalize } from '../lib/text.ts';
import { audioIds } from '../speech/audioIds.ts';
import type { BuildTask } from '../types.ts';
import type { TaskEnv, TaskView } from './ctx.ts';
import { button, h, shake } from './dom.ts';
import { tx, txAttr, txText } from './i18n.ts';
import type { Pair } from './i18n.ts';
import { fitProps, fitToWindow, icon, labelFor, mascotPicture, translateButton } from './kit.ts';
import { L } from './taskText.ts';

const T = {
  yourSentence: { en: 'Your sentence', pl: 'Twoje zdanie' },
  chip: { en: 'Build the sentence', pl: 'Ułóż zdanie' },
  startOver: { en: 'Start over', pl: 'Od nowa' },
  speak: { en: 'Speak', pl: 'Mów' },
} as const satisfies Record<string, Pair>;

export function renderBuild(task: BuildTask, env: TaskEnv): TaskView {
  const { ctx } = env;
  const { player, teacher } = ctx;
  // język uwag bota (przełącznik EN/PL u góry): głos bota zawsze w jednym, wybranym języku
  const langNow = (): FeedbackLang => teacher?.lang() ?? config.teacher.defaultLang;
  const comp = env.companion;
  const items = task.chunks.map((text, id) => ({ id, text }));
  // kropka (pytajnik, wykrzyknik) na końcu zdania stoi poza kawałkami — jak na makiecie: kawałek „dog”, a za polami zostaje „.”
  const endMark = /[.!?]$/.exec(task.text)?.[0] ?? '';
  const lastId = endMark ? items.find((it) => it.text.endsWith(endMark) && task.text.endsWith(it.text))?.id ?? -1 : -1;
  const shown = (id: number): string => (id === lastId ? items[id]!.text.slice(0, -endMark.length) : items[id]!.text);
  let answer: number[] = [];
  let attempts = 0;
  let finished = false;

  const answerBox = txAttr(ctx, h('div', { class: 'answer', 'data-testid': 'answer' }), 'aria-label', T.yourSentence);
  const bank = h('div', { class: 'bank', 'data-testid': 'bank' });
  const feedback = h('p', { class: 'feedback', 'data-testid': 'feedback', 'aria-live': 'polite' });
  const actions = h('div', { class: 'actions-bar center' });

  const chip = (id: number, inAnswer: boolean) => {
    const it = items[id]!;
    return h('button', { type: 'button', class: 'chunk', 'data-testid': 'chunk', 'aria-label': it.text, disabled: finished, onclick: () => toggle(id, inAnswer) }, shown(id));
  };

  const render = () => {
    // miejsca na kawałki, których jeszcze nie ułożono (jak na makiecie): dziecko widzi, ile kawałków ma zdanie
    const slots = finished ? [] : items.slice(answer.length).map(() => h('span', { class: 'slot', 'aria-hidden': 'true' }));
    answerBox.replaceChildren(...answer.map((id) => chip(id, true)), ...slots, ...(lastId >= 0 ? [h('span', { class: 'end-mark', 'aria-hidden': 'true' }, endMark)] : []));
    bank.replaceChildren(...items.filter((it) => !answer.includes(it.id)).map((it) => chip(it.id, false)));
    answerBox.classList.toggle('empty', answer.length === 0);
    if (checkBtn) checkBtn.disabled = answer.length !== items.length || finished;
  };

  const toggle = (id: number, inAnswer: boolean) => {
    if (finished) return;
    answer = inAnswer ? answer.filter((x) => x !== id) : [...answer, id];
    feedback.textContent = '';
    render();
  };

  /**
   * Bot w zadaniach: reakcja po błędnym ułożeniu (i po poprawnym, gdy były potknięcia); wynik nadal ocenia aplikacja.
   * `fallback` — gotowa uwaga z nagrania, gdy bot nic nie mówi (warstwa wyłączona albo odpowiedź zawiodła).
   */
  const reactTo = (passed: boolean, built: string, after?: Promise<unknown>, fallback?: () => void) => {
    const facts = typedAttempt({ no: attempts, passed, typed: built });
    if (comp?.wants(facts)) {
      void comp.react(facts, after ? { after } : {}).then((out) => {
        if (out === 'failed' || out === 'skipped') fallback?.();
      });
    } else fallback?.();
  };
  /** Uwaga po błędnym ułożeniu: mówi ją bot (nagranie f.<klucz>); na ekranie zostaje czerwone pole z potrząśnięciem, bez napisu. */
  const sayWrong = (key: FeedbackKey) => () => void player.playAll([sayKey(key, langNow())], config.teacher.gapMs);

  const finish = (correct: boolean, built: string) => {
    finished = true;
    if (!correct) answer = restoreOrder(task, items); // pokaż rozwiązanie w poprawnej kolejności
    render();
    answerBox.classList.add(correct ? 'right' : 'wrong');
    feedback.textContent = ''; // wynik pokazuje kolor pola (zielone / czerwone z poprawną kolejnością); „Dobrze!” mówi bot głosem ElevenLabs, napisu nie ma
    comp?.quiet(); // reakcja bota przychodzi po zdaniu: najpierw nagranie
    const sentenceClip = { id: task.audioId, fallback: { text: task.text, lang: 'en' as const } };
    const played = correct
      ? player.playAll([sayKey('good', langNow()), sentenceClip], config.teacher.gapMs)
      : player.play(sentenceClip.id, sentenceClip.fallback);
    reactTo(correct, built, played);
    actions.classList.remove('three');
    actions.replaceChildren(
      button([icon('speaker'), tx(ctx, L.listen)], () => void player.play(task.audioId, { text: task.text, lang: 'en' }), { class: 'btn ghost', testid: 'listen' }),
      button([tx(ctx, T.speak), icon('arrow')], () => env.done({ taskId: task.id, kind: 'build', ref: task.sentenceId, correct, attempts }), { class: 'btn primary', testid: 'next' }),
    );
  };

  const check = () => {
    if (finished || answer.length !== items.length) return;
    attempts++;
    const built = answer.map((id) => items[id]!.text).join(' ');
    if (normalize(built) === normalize(task.text)) return finish(true, built);
    shake(answerBox);
    // po kilku nieudanych próbach obok „Sprawdź” pojawia się „Popraw” (pokazuje poprawną kolejność); nic nie rozwiązuje się samo
    const offer = attempts >= config.session.fixAfterAttempts;
    if (offer) offerFix();
    reactTo(false, built, undefined, sayWrong(offer ? 'notYetFix' : 'notYet'));
  };

  const fixBtn = txAttr(ctx, button(tx(ctx, L.showAnswer), () => {
    if (finished) return;
    finish(false, answer.map((id) => items[id]!.text).join(' '));
  }, { class: 'btn ghost', testid: 'fix' }), 'title', L.showAnswerTitle);
  let fixOffered = false;
  const offerFix = () => {
    if (fixOffered || finished) return;
    fixOffered = true;
    actions.classList.add('three');
    actions.replaceChildren(resetBtn, fixBtn, checkBtn);
  };

  const checkBtn = button(tx(ctx, L.check), check, { class: 'btn primary', testid: 'check', disabled: true });
  const resetBtn = button([icon('replay'), tx(ctx, T.startOver)], () => { if (!finished) { answer = []; feedback.textContent = ''; render(); } }, { class: 'btn ghost', testid: 'reset' });
  actions.append(resetBtn, checkBtn);

  // głośnik nad zdaniem: dziecko może posłuchać zdania do ułożenia
  const speaker = h('button', { type: 'button', class: 'speaker-btn', 'data-testid': 'build-listen', onclick: () => {
    comp?.quiet();
    void player.play(task.audioId, { text: task.text, lang: 'en' });
  } }, icon('speaker'));
  labelFor(ctx, speaker, L.listenToSentence);
  const slow = h('button', { type: 'button', class: 'speaker-btn slow', 'data-testid': 'build-listen-slow', onclick: () => {
    comp?.quiet();
    void player.play(task.slowAudioId ?? task.audioId, { text: task.text, lang: 'en', slow: true });
  } }, icon('turtle')); // 🐢 obok głośnika (patch 55)
  labelFor(ctx, slow, L.slowly);

  const prompt = h('p', { class: 'prompt', 'data-testid': 'prompt' }, tx(ctx, L.inPolishLabel), h('strong', null, task.pl));
  const plToggle = translateButton(true, (on) => { prompt.hidden = !on; }, ctx);

  const el = h(
    'section',
    { class: 'task build exercise screen', 'data-task': 'build', 'data-testid': 'task-build', ...fitProps(Math.round(task.text.length * 1.4)) }, // klocki łamią się na wiele rzędów, więc zdanie liczy się jak dłuższe
    h('div', { class: 'tools-row' }, txText(ctx, h('p', { class: 'chip' }), T.chip), plToggle),
    h('div', { class: 'ex-main screen-main' },
      h('div', { class: 'speaker-row' }, speaker, slow),
      h('div', { class: 'split' },
        h('div', { class: 'col' },
          prompt,
          answerBox,
          bank,
          feedback,
        ),
        h('div', { class: 'side' }, mascotPicture('step-final')),
      ),
    ),
    actions,
  );
  render();
  const fit = fitToWindow(el);
  return { el, destroy: fit.dispose };
}

/** Kolejność indeksów kawałków odpowiadająca poprawnemu zdaniu (kawałki mogą się powtarzać). */
function restoreOrder(task: BuildTask, items: { id: number; text: string }[]): number[] {
  const pool = items.slice();
  const out: number[] = [];
  let rest = task.text;
  while (rest.length > 0 && pool.length > 0) {
    const idx = pool.findIndex((it) => rest === it.text || rest.startsWith(it.text + ' '));
    if (idx === -1) break;
    const it = pool.splice(idx, 1)[0]!;
    out.push(it.id);
    rest = rest.slice(it.text.length).trimStart();
  }
  return out;
}
